'use client';
import React, { useCallback, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Camera, Upload, X, ImageIcon, CheckCircle2 } from 'lucide-react';
import { QuoteFormData } from './QuickQuoteWizard';

const projectTypes = [
  'Interior Painting',
  'Exterior Painting',
  'Pressure Washing',
  'Flooring Installation',
  'Drywall Repair',
  'General Handyman',
];

interface Step1Props {
  form: UseFormReturn<QuoteFormData>;
  onNext: () => void;
}

export default function Step1PhotoUpload({ form, onNext }: Step1Props) {
  const { setValue, watch, formState: { errors } } = form;
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const photoPreviewUrl = watch('photoPreviewUrl');
  const projectType = watch('projectType');

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setValue('photo', file);
    setValue('photoPreviewUrl', url);
  }, [setValue]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const clearPhoto = () => {
    setValue('photo', null);
    setValue('photoPreviewUrl', '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canProceed = !!photoPreviewUrl && !!projectType;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-800 text-white mb-1">Show us the project</h2>
        <p className="text-[#888] text-sm">Upload a photo so our AI can generate an accurate estimate for you.</p>
      </div>

      {/* Photo Upload Zone */}
      {!photoPreviewUrl ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            relative w-full rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
            flex flex-col items-center justify-center gap-4 py-12 px-6 text-center
            ${isDragging
              ? 'border-blue-500 bg-blue-500/10' :'border-[#2A2A2A] bg-[#0D0D0D] hover:border-[#3A3A3A] hover:bg-[#111]'
            }
          `}
        >
          <div className="w-14 h-14 rounded-2xl bg-blue-600/15 border border-blue-600/25 flex items-center justify-center">
            <ImageIcon size={28} className="text-blue-400" />
          </div>
          <div>
            <p className="text-white font-600 mb-1">Drop your photo here</p>
            <p className="text-[#666] text-sm">or tap to browse from camera roll</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              className="flex items-center gap-2 leverage-btn-primary text-sm py-2.5 px-5"
            >
              <Upload size={16} />
              Upload Photo
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
              className="flex items-center gap-2 leverage-btn-secondary text-sm py-2.5 px-5"
            >
              <Camera size={16} />
              Take Photo
            </button>
          </div>
          <p className="text-[11px] text-[#444]">JPG, PNG, HEIC up to 20MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="sr-only"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileInput}
            className="sr-only"
          />
        </div>
      ) : (
        <div className="relative rounded-xl overflow-hidden bg-[#0D0D0D] border border-[#2A2A2A]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={photoPreviewUrl}
            alt="Uploaded project photo showing the work area"
            className="w-full h-56 object-cover"
          />
          <div className="absolute top-3 right-3 flex gap-2">
            <div className="flex items-center gap-1.5 bg-black/70 backdrop-blur-sm rounded-full px-3 py-1.5">
              <CheckCircle2 size={14} className="text-emerald-400" />
              <span className="text-xs text-white font-600">Photo ready</span>
            </div>
            <button
              onClick={clearPhoto}
              className="w-8 h-8 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-red-500/80 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <p className="text-xs text-[#999]">AI will analyze this image for the estimate</p>
          </div>
        </div>
      )}

      {/* Project Type */}
      <div>
        <label className="leverage-label">Project Type *</label>
        <div className="grid grid-cols-2 gap-2">
          {projectTypes.map((type) => (
            <button
              key={`type-${type}`}
              type="button"
              onClick={() => setValue('projectType', type)}
              className={`
                px-3 py-3 rounded-lg text-sm font-500 text-left transition-all duration-150 border
                ${projectType === type
                  ? 'bg-blue-600/20 border-blue-600/50 text-blue-300' :'bg-[#0D0D0D] border-[#2A2A2A] text-[#888] hover:border-[#3A3A3A] hover:text-white'
                }
              `}
            >
              {type}
            </button>
          ))}
        </div>
        {!projectType && (
          <p className="text-[#555] text-xs mt-1.5">Select the type of work needed</p>
        )}
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={!canProceed}
        className="leverage-btn-primary w-full py-4 text-base"
      >
        Continue to Questions →
      </button>
    </div>
  );
}